<script setup>
import { computed, onMounted, ref } from "vue";
import {
  fetchUsers,
  getMediaUrl,
  vectorizeMediaComplianceRuleImage,
  previewMediaComplianceRuleMatches,
} from "@/services/backofficeClient";
import { useRouter } from "vue-router";

const DEFAULT_PAGE_SIZE = 50;
const STATUS_OPTIONS = ["active", "blocked", "deleted", "ghosted"];
const GENDER_OPTIONS = ["female", "male", "nonBinary"];
const VERIFICATION_STATUS_OPTIONS = [
  "waiting_for_review",
  "verified",
  "rejected_code",
  "rejected_selfie",
];
const SORT_OPTIONS = [
  { value: "lastLoginDatetime", label: "Last login" },
  { value: "createdAt", label: "Created at" },
];

const users = ref([]);
const searchTerm = ref("");
const statusFilter = ref("");
const genderFilter = ref("");
const verificationStatusFilter = ref("");
const sortBy = ref(SORT_OPTIONS[0].value);
const sortOrder = ref("desc");
const currentPage = ref(1);
const pagination = ref({
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalItems: 0,
  totalPages: 1,
  hasNext: false,
});
const isLoading = ref(true);
const errorMessage = ref("");
const router = useRouter();

const normalizedPageSize = computed(() => {
  const size = Number(pagination.value.pageSize);
  return Number.isFinite(size) && size > 0 ? size : DEFAULT_PAGE_SIZE;
});
const totalItems = computed(() => {
  const total = Number(pagination.value.totalItems);
  return Number.isFinite(total) && total >= 0 ? total : 0;
});
const totalPages = computed(() => {
  const pages = Number(pagination.value.totalPages);
  return Number.isFinite(pages) && pages > 0 ? pages : 1;
});
const rangeStart = computed(() => {
  if (totalItems.value === 0) return 0;
  return (Math.max(1, currentPage.value) - 1) * normalizedPageSize.value + 1;
});
const rangeEnd = computed(() => {
  if (totalItems.value === 0) return 0;
  return Math.min(currentPage.value * normalizedPageSize.value, totalItems.value);
});
const canGoPrev = computed(() => currentPage.value > 1 && !isLoading.value);
const canGoNext = computed(
  () => currentPage.value < totalPages.value && !isLoading.value
);
const isEmpty = computed(
  () => !isLoading.value && !errorMessage.value && users.value.length === 0
);

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const deriveLocation = (item) => {
  if (item.locationLabel) return item.locationLabel;
  const address = item.address;
  let city;
  let country;
  if (address && typeof address === "object") {
    const cityValue = address.city;
    const countryValue = address.country;
    city =
      typeof cityValue === "object"
        ? cityValue?.en || cityValue?.name
        : cityValue;
    country =
      typeof countryValue === "object"
        ? countryValue?.en || countryValue?.name
        : countryValue;
  }
  const cityText = city ? String(city) : "";
  const countryText = country ? String(country) : "";
  if (cityText && countryText) return `${cityText}, ${countryText}`;
  if (cityText) return cityText;
  if (countryText) return countryText;
  return "--";
};

const deriveMediaUrl = (item) => {
  if (item.photoUrl) return item.photoUrl;
  const atoms = Array.isArray(item.atoms) ? item.atoms : [];
  const photoAtom = atoms.find((atom) => atom?.attribute === "photo");
  if (photoAtom?.mediaPath) {
    return getMediaUrl(photoAtom.mediaPath);
  }
  return null;
};

const parseUser = (item) => {
  const lastLogin =
    item.lastLoginDatetime ||
    item.lastLoginAt ||
    item.lastLoginDate ||
    item.lastLogin;
  return {
    id: item.id ? String(item.id) : "",
    name: item.name
      ? String(item.name)
      : item.fullName ||
      item.displayName ||
      [item.firstName, item.lastName]
        .filter(Boolean)
        .map((v) => String(v))
        .join(" ")
        .trim(),
    gender: item.gender ? String(item.gender) : "--",
    status: item.status ? String(item.status) : item.state ? String(item.state) : "--",
    verificationStatus: item.verificationStatus || item.verifyStatus || "",
    photoUrl: deriveMediaUrl(item),
    lastLoginAt: parseDate(lastLogin),
    createdAt: parseDate(item.createdAt),
    location: deriveLocation(item),
    age: Number.isFinite(Number(item.age)) ? Number(item.age) : null,
    birthday: parseDate(item.birthdate || item.birthday),
  };
};

const resolvePagination = (rawPagination, itemsLength) => {
  const fallbackTotalPages = Math.max(
    1,
    Math.ceil((itemsLength || 0) / DEFAULT_PAGE_SIZE)
  );
  if (rawPagination && typeof rawPagination === "object") {
    const page = Number(rawPagination.page) || currentPage.value;
    const pageSize = Number(rawPagination.pageSize) || DEFAULT_PAGE_SIZE;
    const totalItemsValue =
      Number(rawPagination.totalItems) || Number(itemsLength) || 0;
    const totalPagesValue =
      Number(rawPagination.totalPages) || fallbackTotalPages;
    return {
      page: page > 0 ? page : 1,
      pageSize: pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE,
      totalItems: totalItemsValue >= 0 ? totalItemsValue : 0,
      totalPages: totalPagesValue > 0 ? totalPagesValue : fallbackTotalPages,
      hasNext:
        rawPagination.hasNext === true
          ? true
          : (page || 1) < (totalPagesValue || fallbackTotalPages),
    };
  }
  return {
    page: currentPage.value,
    pageSize: DEFAULT_PAGE_SIZE,
    totalItems: itemsLength,
    totalPages: fallbackTotalPages,
    hasNext: fallbackTotalPages > currentPage.value,
  };
};

const loadUsers = async () => {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    const payload = await fetchUsers({
      page: currentPage.value,
      pageSize: DEFAULT_PAGE_SIZE,
      search: searchTerm.value,
      sortBy: sortBy.value,
      sortOrder: sortOrder.value,
      status: statusFilter.value || undefined,
      gender: genderFilter.value || undefined,
      verificationStatus: verificationStatusFilter.value || undefined,
    });

    const rawItems = Array.isArray(payload) ? payload : payload?.items;
    const parsed = Array.isArray(rawItems)
      ? rawItems
        .filter((item) => item && typeof item === "object")
        .map((item) => parseUser(item))
      : [];
    const meta = resolvePagination(payload?.pagination, parsed.length);

    pagination.value = meta;
    currentPage.value = Math.min(
      Math.max(1, meta.page || 1),
      Math.max(1, meta.totalPages || 1)
    );
    users.value = parsed;
  } catch (error) {
    console.error("Failed to fetch users", error);
    errorMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load users. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

const submitSearch = () => {
  currentPage.value = 1;
  loadUsers();
};

const resetFilters = () => {
  searchTerm.value = "";
  statusFilter.value = "";
  genderFilter.value = "";
  verificationStatusFilter.value = "";
  sortBy.value = SORT_OPTIONS[0].value;
  sortOrder.value = "desc";
  currentPage.value = 1;
  loadUsers();
};

const changePage = (delta) => {
  const nextPage = currentPage.value + delta;
  if (nextPage < 1 || nextPage === currentPage.value) {
    return;
  }
  currentPage.value = nextPage;
  loadUsers();
};

const goToUser = (user) => {
  if (!user?.id || isLoading.value) return;
  router.push(`/users/${user.id}`);
};

const toggleSortOrder = () => {
  sortOrder.value = sortOrder.value === "desc" ? "asc" : "desc";
  currentPage.value = 1;
  loadUsers();
};

const formatDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "--";
  }
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const statusBadgeClass = (status) => {
  const normalized = status ? String(status).toLowerCase() : "";
  const base = "badge text-xxs";
  switch (normalized) {
    case "active":
      return `${base} bg-gradient-success text-white`;
    case "blocked":
      return `${base} bg-gradient-danger text-white`;
    case "ghosted":
      return `${base} bg-dark text-white`;
    case "deleted":
      return `${base} bg-secondary text-white`;
    default:
      return `${base} bg-light text-dark border`;
  }
};

const verificationBadgeClass = (status) => {
  const normalized = status ? String(status).toLowerCase() : "";
  const base = "badge text-xxs";
  switch (normalized) {
    case "verified":
      return `${base} bg-gradient-success text-white`;
    case "waiting_for_review":
      return `${base} bg-gradient-warning text-white`;
    case "rejected_code":
    case "rejected_selfie":
      return `${base} bg-gradient-danger text-white`;
    default:
      return `${base} bg-light text-dark border`;
  }
};

const formatVerificationStatus = (status) => {
  if (!status) return "";
  const spaced = status.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const formatAge = (user) => {
  const age = Number.isFinite(user.age) ? Number(user.age) : null;
  const birthdayText = user.birthday ? formatDateOnly(user.birthday) : null;
  if (age == null && !birthdayText) return "--";
  if (age != null && birthdayText) {
    return `${age} (${birthdayText})`;
  }
  return age != null ? age : birthdayText || "--";
};

const formatDateOnly = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "--";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const PHOTO_SEARCH_PAGE_SIZE = 24;
const DEFAULT_PHOTO_SEARCH_THRESHOLD = 0.9;

const searchByPhotoModalOpen = ref(false);
const photoSearchMode = ref("link");
const photoSearchLink = ref("");
const photoSearchFileInput = ref(null);
const photoSearchFile = ref(null);
const photoSearchPreviewUrl = ref("");
const photoSearchLoading = ref(false);
const photoSearchError = ref("");
const photoSearchResults = ref(null);
const photoSearchVectors = ref(null);
const photoSearchPage = ref(1);
const photoSearchPagination = ref({
  page: 1,
  pageSize: PHOTO_SEARCH_PAGE_SIZE,
  totalItems: 0,
  totalPages: 0,
  hasNext: false,
});

const revokePhotoSearchPreview = () => {
  if (photoSearchPreviewUrl.value) {
    URL.revokeObjectURL(photoSearchPreviewUrl.value);
    photoSearchPreviewUrl.value = "";
  }
};

const invalidatePhotoSearch = () => {
  photoSearchVectors.value = null;
  photoSearchResults.value = null;
  photoSearchError.value = "";
};

const openPhotoSearchModal = () => {
  searchByPhotoModalOpen.value = true;
  photoSearchMode.value = "link";
  photoSearchLink.value = "";
  photoSearchFile.value = null;
  revokePhotoSearchPreview();
  invalidatePhotoSearch();
  photoSearchPage.value = 1;
};

const closePhotoSearchModal = () => {
  if (photoSearchLoading.value) return;
  searchByPhotoModalOpen.value = false;
  revokePhotoSearchPreview();
};

const onPhotoSearchFileChange = (event) => {
  const file = event.target.files?.[0];
  event.target.value = "";
  if (!file) return;
  photoSearchFile.value = file;
  photoSearchLink.value = "";
  revokePhotoSearchPreview();
  photoSearchPreviewUrl.value = URL.createObjectURL(file);
  invalidatePhotoSearch();
};

const runPhotoSearch = async (page = 1) => {
  photoSearchError.value = "";
  photoSearchLoading.value = true;
  try {
    let vectors = photoSearchVectors.value;
    if (!vectors) {
      let source = photoSearchFile.value;
      if (!source) {
        const link = photoSearchLink.value.trim();
        if (!link) {
          throw new Error("Paste a CDN link or upload a photo first.");
        }
        const response = await fetch(link);
        if (!response.ok) {
          throw new Error(
            `Failed to load image from link (status ${response.status}).`
          );
        }
        source = await response.blob();
      }
      const vectorized = await vectorizeMediaComplianceRuleImage(source);
      vectors = (Array.isArray(vectorized?.vectors) ? vectorized.vectors : []).map(
        (v) => ({
          type: v.type,
          vector: v.vector,
          threshold: v.threshold ?? DEFAULT_PHOTO_SEARCH_THRESHOLD,
        })
      );
      if (!vectors.length) {
        throw new Error(
          "Could not extract a match vector from this photo."
        );
      }
      photoSearchVectors.value = vectors;
    }

    const payload = await previewMediaComplianceRuleMatches(vectors, {
      page,
      pageSize: PHOTO_SEARCH_PAGE_SIZE,
    });
    photoSearchResults.value = Array.isArray(payload?.items) ? payload.items : [];
    photoSearchPagination.value = payload?.pagination || {
      page,
      pageSize: PHOTO_SEARCH_PAGE_SIZE,
      totalItems: photoSearchResults.value.length,
      totalPages: photoSearchResults.value.length ? 1 : 0,
      hasNext: false,
    };
    photoSearchPage.value = photoSearchPagination.value.page || page;
  } catch (error) {
    console.error("Failed to search users by photo", error);
    photoSearchError.value =
      error?.payload?.message || error?.message || "Failed to search by photo.";
    photoSearchResults.value = null;
  } finally {
    photoSearchLoading.value = false;
  }
};

const changePhotoSearchPage = (delta) => {
  const nextPage = photoSearchPage.value + delta;
  if (nextPage < 1 || nextPage > photoSearchPagination.value.totalPages) return;
  runPhotoSearch(nextPage);
};

onMounted(loadUsers);
</script>

<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card shadow-sm">
          <div class="card-header pb-0 border-0">
            <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
              <div>
                <h6 class="mb-1">User list</h6>
                <p class="text-xs text-secondary mb-0">
                  Page {{ currentPage }} of {{ totalPages }} · {{ totalItems }} total
                </p>
              </div>
              <div class="mt-3 mt-lg-0 d-flex align-items-center gap-2 flex-wrap">
                <button class="btn btn-sm btn-outline-success mb-0" type="button" :disabled="isLoading"
                  @click="openPhotoSearchModal">
                  Search by photo
                </button>
                <button class="btn btn-sm btn-outline-secondary mb-0" type="button" :disabled="isLoading"
                  @click="resetFilters">
                  Reset filters
                </button>
              </div>
            </div>

            <form class="row g-2 align-items-center mt-3" @submit.prevent="submitSearch">
              <div class="col-12 col-lg-3">
                <div class="input-group input-group-sm">
                  <span class="input-group-text bg-white border-end-0">
                    <i class="ni ni-zoom-split-in"></i>
                  </span>
                  <input type="text" class="form-control border-start-0" placeholder="Search users by name or email"
                    v-model="searchTerm" :disabled="isLoading" />
                  <button class="btn btn-outline-secondary mb-0" type="button" :disabled="!searchTerm || isLoading"
                    @click="() => { searchTerm = ''; submitSearch(); }">
                    Clear
                  </button>
                  <button class="btn btn-success mb-0" type="submit" :disabled="isLoading">
                    <i class="fas fa-search"></i>
                  </button>
                </div>
              </div>

              <div class="col-6 col-lg-1">
                <select class="form-select form-select-sm" v-model="sortBy" :disabled="isLoading"
                  @change="() => { currentPage = 1; loadUsers(); }">
                  <option v-for="option in SORT_OPTIONS" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <div class="col-auto">
                <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="isLoading"
                  @click="toggleSortOrder" :title="sortOrder === 'desc' ? 'Descending' : 'Ascending'">
                  <i :class="sortOrder === 'desc' ? 'ni ni-bold-down' : 'ni ni-bold-up'"></i>
                </button>
              </div>

              <div class="col-6 col-lg-2">
                <select class="form-select form-select-sm" v-model="statusFilter" :disabled="isLoading"
                  @change="() => { currentPage = 1; loadUsers(); }">
                  <option value="">All statuses</option>
                  <option v-for="status in STATUS_OPTIONS" :key="status" :value="status">
                    {{ status.charAt(0).toUpperCase() + status.slice(1) }}
                  </option>
                </select>
              </div>

              <div class="col-6 col-lg-2">
                <select class="form-select form-select-sm" v-model="verificationStatusFilter" :disabled="isLoading"
                  @change="() => { currentPage = 1; loadUsers(); }">
                  <option value="">All verification statuses</option>
                  <option v-for="status in VERIFICATION_STATUS_OPTIONS" :key="status" :value="status">
                    {{ formatVerificationStatus(status) }}
                  </option>
                </select>
              </div>

              <div class="col-6 col-lg-2">
                <select class="form-select form-select-sm" v-model="genderFilter" :disabled="isLoading"
                  @change="() => { currentPage = 1; loadUsers(); }">
                  <option value="">All genders</option>
                  <option v-for="gender in GENDER_OPTIONS" :key="gender" :value="gender">
                    {{ gender.charAt(0).toUpperCase() + gender.slice(1) }}
                  </option>
                </select>
              </div>
            </form>
          </div>

          <div class="card-body px-0 pt-0 pb-4">
            <div v-if="isLoading" class="text-center py-5">
              <div class="spinner-border text-success" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3 mb-0 text-sm text-secondary">
                Loading users...
              </p>
            </div>

            <div v-else-if="errorMessage" class="text-center py-5 px-4">
              <div class="icon icon-shape bg-gradient-danger shadow text-center border-radius-md text-white mx-auto">
                <i class="ni ni-fat-remove"></i>
              </div>
              <p class="mt-3 mb-2 text-sm text-dark">{{ errorMessage }}</p>
              <button type="button" class="btn btn-sm btn-success" @click="loadUsers">
                Try again
              </button>
            </div>

            <div v-else-if="isEmpty" class="text-center py-5 px-4">
              <div class="icon icon-shape bg-gradient-success shadow text-center border-radius-md text-white mx-auto">
                <i class="ni ni-circle-08"></i>
              </div>
              <h6 class="mt-3">No users found</h6>
              <p class="text-sm text-secondary mb-3">
                Adjust your filters or try refreshing the list.
              </p>
              <button type="button" class="btn btn-sm btn-outline-success" @click="loadUsers">
                Reload
              </button>
            </div>

            <div v-else class="px-3">
              <div class="table-responsive">
                <table class="table align-items-center mb-0">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Name
                      </th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Gender
                      </th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Status
                      </th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Verification
                      </th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Photo
                      </th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                        Last login
                      </th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                        Created at
                      </th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Location
                      </th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Age (Birthday)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="user in users" :key="`${user.id}-${user.name}`" @click="goToUser(user)"
                      :style="user.id ? 'cursor: pointer' : ''">
                      <td>
                        <div class="d-flex flex-column">
                          <RouterLink v-if="user.id" :to="`/users/${user.id}`"
                            class="text-sm font-weight-bold text-reset text-decoration-none" @click.stop>
                            {{ user.name || "--" }}
                          </RouterLink>
                          <span v-else class="text-sm font-weight-bold">
                            {{ user.name || "--" }}
                          </span>
                          <span class="text-xs text-secondary">
                            {{ user.id || "" }}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span class="text-sm">{{ user.gender || "--" }}</span>
                      </td>
                      <td>
                        <span :class="statusBadgeClass(user.status)">
                          {{ user.status || "--" }}
                        </span>
                      </td>
                      <td>
                        <span v-if="user.verificationStatus"
                          :class="verificationBadgeClass(user.verificationStatus)">
                          {{ formatVerificationStatus(user.verificationStatus) }}
                        </span>
                      </td>
                      <td>
                        <div
                          class="avatar avatar-lg rounded-circle bg-light border d-flex align-items-center justify-content-center overflow-hidden">
                          <img v-if="user.photoUrl" :src="user.photoUrl" alt="avatar"
                            class="w-100 h-100 object-fit-cover" />
                          <i v-else class="ni ni-single-02 text-success"></i>
                        </div>
                      </td>
                      <td class="text-center">
                        <span class="text-sm">{{ formatDate(user.lastLoginAt) }}</span>
                      </td>
                      <td class="text-center">
                        <span class="text-sm">{{ formatDate(user.createdAt) }}</span>
                      </td>
                      <td>
                        <span class="text-sm">{{ user.location || "--" }}</span>
                      </td>
                      <td>
                        <span class="text-sm">{{ formatAge(user) }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="d-flex align-items-center flex-wrap gap-2 pt-3 border-top">
                <p class="mb-0 text-sm">
                  {{
                    totalItems === 0
                      ? "No results"
                      : `Showing ${rangeStart}-${rangeEnd} of ${totalItems}`
                  }}
                </p>
                <div class="ms-auto d-flex align-items-center gap-2">
                  <span class="text-xs text-secondary">
                    Page {{ currentPage }} of {{ totalPages }}
                  </span>
                  <div class="btn-group btn-group-sm">
                    <button type="button" class="btn btn-outline-secondary" :disabled="!canGoPrev"
                      @click="changePage(-1)">
                      Previous
                    </button>
                    <button type="button" class="btn btn-outline-secondary" :disabled="!canGoNext"
                      @click="changePage(1)">
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="searchByPhotoModalOpen" class="modal fade show d-block" tabindex="-1"
      style="background: rgba(0, 0, 0, 0.5);">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h6 class="modal-title">Search user by photo</h6>
            <button type="button" class="btn-close" aria-label="Close" @click="closePhotoSearchModal"
              :disabled="photoSearchLoading"></button>
          </div>
          <div class="modal-body">
            <div class="btn-group btn-group-sm mb-3" role="group">
              <button type="button" class="btn"
                :class="photoSearchMode === 'link' ? 'btn-success' : 'btn-outline-success'"
                :disabled="photoSearchLoading" @click="photoSearchMode = 'link'">
                Paste CDN link
              </button>
              <button type="button" class="btn"
                :class="photoSearchMode === 'upload' ? 'btn-success' : 'btn-outline-success'"
                :disabled="photoSearchLoading" @click="photoSearchMode = 'upload'">
                Upload photo
              </button>
            </div>

            <div v-if="photoSearchMode === 'link'" class="mb-3">
              <label class="form-label text-sm fw-semibold">Internal CDN link</label>
              <input type="text" class="form-control"
                placeholder="https://admin-.../admin/cdn/ugc/.../photo/....webp" v-model="photoSearchLink"
                :disabled="photoSearchLoading" @input="invalidatePhotoSearch" />
              <p class="text-xxs text-secondary mt-1 mb-0">
                Paste a user's internal CDN photo link (only reachable from within the admin app).
              </p>
            </div>
            <div v-else class="mb-3">
              <input ref="photoSearchFileInput" type="file" accept="image/*" class="d-none"
                @change="onPhotoSearchFileChange" />
              <button class="btn btn-sm btn-outline-success mb-0" type="button" :disabled="photoSearchLoading"
                @click="photoSearchFileInput.click()">
                Choose photo
              </button>
              <span v-if="photoSearchFile" class="text-xs text-secondary ms-2">{{ photoSearchFile.name }}</span>
            </div>

            <div v-if="photoSearchPreviewUrl" class="mb-3">
              <img :src="photoSearchPreviewUrl" class="rounded" style="max-height: 160px" />
            </div>

            <div class="d-flex justify-content-end mb-3">
              <button type="button" class="btn btn-sm btn-success mb-0"
                :disabled="photoSearchLoading || (!photoSearchLink.trim() && !photoSearchFile)"
                @click="runPhotoSearch(1)">
                {{ photoSearchLoading ? "Searching..." : "Search" }}
              </button>
            </div>

            <div v-if="photoSearchError" class="alert alert-danger py-2 px-3">
              <span class="text-sm">{{ photoSearchError }}</span>
            </div>

            <div v-if="photoSearchResults && !photoSearchResults.length" class="text-xs text-secondary">
              No matching users found for this photo.
            </div>
            <template v-else-if="photoSearchResults && photoSearchResults.length">
              <div class="d-flex flex-wrap gap-3">
                <div v-for="item in photoSearchResults" :key="item.path" class="border rounded p-2 text-center"
                  style="width: 120px">
                  <img :src="getMediaUrl(item.path)" class="w-100 rounded mb-1"
                    style="height: 90px; object-fit: cover" />
                  <div class="text-xs font-weight-bold">
                    {{ (item.similarityScore * 100).toFixed(1) }}%
                  </div>
                  <RouterLink :to="`/users/${item.userId}`" class="text-xs text-truncate d-block"
                    :title="item.user?.name" @click="closePhotoSearchModal">
                    {{ item.user?.name || item.userId }}
                  </RouterLink>
                  <span class="badge text-xxs mt-1"
                    :class="item.user?.status === 'active' ? 'bg-gradient-success' : 'bg-gradient-secondary'">
                    {{ item.user?.status || "unknown" }}
                  </span>
                </div>
              </div>

              <div class="d-flex align-items-center flex-wrap gap-2 pt-3 mt-2 border-top">
                <p class="mb-0 text-xs text-secondary">
                  {{ photoSearchPagination.totalItems }} match{{ photoSearchPagination.totalItems === 1 ? "" : "es" }}
                </p>
                <div class="ms-auto d-flex align-items-center gap-2">
                  <span class="text-xs text-secondary">
                    Page {{ photoSearchPagination.page }} of {{ Math.max(photoSearchPagination.totalPages, 1) }}
                  </span>
                  <div class="btn-group btn-group-sm">
                    <button type="button" class="btn btn-outline-secondary"
                      :disabled="photoSearchLoading || photoSearchPage <= 1" @click="changePhotoSearchPage(-1)">
                      Previous
                    </button>
                    <button type="button" class="btn btn-outline-secondary"
                      :disabled="photoSearchLoading || photoSearchPage >= photoSearchPagination.totalPages"
                      @click="changePhotoSearchPage(1)">
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="closePhotoSearchModal"
              :disabled="photoSearchLoading">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
